/**
 * Email Notification Service
 * Handles sending emails for payment confirmations, subscription updates, etc.
 *
 * This is a placeholder service that logs email content.
 * To enable actual email sending, integrate with:
 * - Resend (https://resend.com) - Recommended
 * - SendGrid
 * - AWS SES
 * - Or use Supabase Edge Functions
 */

import { supabase } from './supabase'

interface EmailRecipient {
  email: string
  name: string
}

interface EmailData {
  to: EmailRecipient[]
  subject: string
  htmlBody: string
  textBody: string
  from?: string
  replyTo?: string
}

export class EmailService {
  private static readonly FROM_EMAIL = 'noreply@faithyoke.comm'
  private static readonly FROM_NAME = 'FaithYoke'
  private static readonly REPLY_TO = 'support@faithyoke.comm'

  /**
   * Send an email using Resend API
   */
  static async sendEmail(emailData: EmailData): Promise<{ success: boolean; messageId?: string; error?: string }> {
    try {
      const resendApiKey = import.meta.env.VITE_RESEND_API_KEY

      if (!resendApiKey) {
        console.warn('Resend API key not configured. Logging email to console only.')
        console.log('='.repeat(80))
        console.log('📧 EMAIL NOTIFICATION (Not Sent - No API Key)')
        console.log('='.repeat(80))
        console.log('From:', `${this.FROM_NAME} <${emailData.from || this.FROM_EMAIL}>`)
        console.log('To:', emailData.to.map(r => `${r.name} <${r.email}>`).join(', '))
        console.log('Subject:', emailData.subject)
        console.log('='.repeat(80))

        // Log to database for audit trail
        await this.logEmail(emailData)

        return {
          success: true,
          messageId: `EMAIL-${Date.now()}`
        }
      }

      // Send email via Resend
      const response = await fetch('https://api.resend.com/emails', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${resendApiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          from: `${this.FROM_NAME} <${emailData.from || this.FROM_EMAIL}>`,
          to: emailData.to.map(r => r.email),
          subject: emailData.subject,
          html: emailData.htmlBody,
          text: emailData.textBody,
          reply_to: emailData.replyTo || this.REPLY_TO
        })
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.message || 'Failed to send email via Resend')
      }

      console.log('✅ Email sent successfully via Resend:', result.id)

      // Log to database for audit trail
      await this.logEmail(emailData)

      return {
        success: true,
        messageId: result.id
      }
    } catch (error: any) {
      console.error('Error sending email:', error)

      // Log failed email attempt
      try {
        await this.logEmail(emailData, 'failed', error.message)
      } catch (logError) {
        console.error('Failed to log email error:', logError)
      }

      return {
        success: false,
        error: error.message || 'Failed to send email'
      }
    }
  }

  /**
   * Log email to database for audit trail
   */
  private static async logEmail(
    emailData: EmailData,
    status: 'sent' | 'failed' | 'pending' = 'sent',
    errorMessage?: string
  ): Promise<void> {
    try {
      await supabase.from('email_logs').insert({
        to_addresses: emailData.to.map(r => r.email),
        from_address: emailData.from || this.FROM_EMAIL,
        subject: emailData.subject,
        html_body: emailData.htmlBody,
        text_body: emailData.textBody,
        sent_at: new Date().toISOString(),
        status: status,
        error_message: errorMessage || null,
        provider: 'resend',
        email_type: 'payment_confirmation'
      })
    } catch (error) {
      console.warn('Failed to log email:', error)
    }
  }

  /**
   * Send payment confirmation email to church admin
   */
  static async sendPaymentConfirmation(params: {
    churchName: string
    churchEmail: string
    adminName: string
    amount: number
    currency: string
    paymentMethod: string
    transactionReference: string
    paymentDate: string
    receiptNumber: string
  }): Promise<{ success: boolean; error?: string }> {
    const {
      churchName,
      churchEmail,
      adminName,
      amount,
      currency,
      paymentMethod,
      transactionReference,
      paymentDate,
      receiptNumber
    } = params

    const htmlBody = this.generatePaymentConfirmationHTML(params)
    const textBody = this.generatePaymentConfirmationText(params)

    return await this.sendEmail({
      to: [{ email: churchEmail, name: adminName }],
      subject: `Payment Received - ${receiptNumber}`,
      htmlBody,
      textBody
    })
  }

  /**
   * Generate HTML email for payment confirmation
   */
  private static generatePaymentConfirmationHTML(params: {
    churchName: string
    adminName: string
    amount: number
    currency: string
    paymentMethod: string
    transactionReference: string
    paymentDate: string
    receiptNumber: string
  }): string {
    const formattedAmount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: params.currency
    }).format(params.amount)

    const formattedDate = new Date(params.paymentDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Payment Confirmation</title>
  <style>
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 600px;
      margin: 0 auto;
      padding: 20px;
      background-color: #f4f4f4;
    }
    .container {
      background-color: #ffffff;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 30px;
      text-align: center;
    }
    .header h1 {
      margin: 0;
      font-size: 28px;
    }
    .content {
      padding: 30px;
    }
    .highlight-box {
      background-color: #f0f9ff;
      border-left: 4px solid #3b82f6;
      padding: 15px;
      margin: 20px 0;
      border-radius: 4px;
    }
    .amount {
      font-size: 32px;
      font-weight: bold;
      color: #10b981;
      text-align: center;
      margin: 20px 0;
    }
    .details {
      background-color: #f9fafb;
      padding: 20px;
      border-radius: 8px;
      margin: 20px 0;
    }
    .detail-row {
      display: flex;
      justify-content: space-between;
      padding: 8px 0;
      border-bottom: 1px solid #e5e7eb;
    }
    .detail-row:last-child {
      border-bottom: none;
    }
    .detail-label {
      font-weight: 600;
      color: #6b7280;
    }
    .detail-value {
      color: #111827;
      text-align: right;
    }
    .footer {
      background-color: #f9fafb;
      padding: 20px;
      text-align: center;
      font-size: 14px;
      color: #6b7280;
    }
    .button {
      display: inline-block;
      padding: 12px 24px;
      background-color: #3b82f6;
      color: white;
      text-decoration: none;
      border-radius: 6px;
      margin: 20px 0;
      font-weight: 600;
    }
    .success-badge {
      display: inline-block;
      padding: 6px 12px;
      background-color: #d1fae5;
      color: #065f46;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>✅ Payment Received</h1>
      <p style="margin: 10px 0 0 0; font-size: 16px; opacity: 0.9;">Thank you for your payment</p>
    </div>

    <div class="content">
      <p>Dear ${params.adminName},</p>

      <p>We're pleased to confirm that we have received your payment for <strong>${params.churchName}</strong>.</p>

      <div class="amount">${formattedAmount}</div>

      <div class="highlight-box">
        <p style="margin: 0; text-align: center;">
          <span class="success-badge">Payment Confirmed</span>
        </p>
      </div>

      <div class="details">
        <h3 style="margin-top: 0;">Payment Details</h3>
        <div class="detail-row">
          <span class="detail-label">Receipt Number:</span>
          <span class="detail-value" style="font-family: monospace; font-weight: bold;">${params.receiptNumber}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Payment Date:</span>
          <span class="detail-value">${formattedDate}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Amount Paid:</span>
          <span class="detail-value" style="font-weight: bold;">${formattedAmount}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Payment Method:</span>
          <span class="detail-value">${params.paymentMethod.replace('_', ' ').toUpperCase()}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Reference:</span>
          <span class="detail-value" style="font-family: monospace;">${params.transactionReference}</span>
        </div>
      </div>

      <p style="text-align: center;">
        <a href="https://admin.faithyoke.comm/settings?tab=subscription" class="button">
          View Subscription Details
        </a>
      </p>

      <p>Your subscription has been activated and will remain active for one year from the payment date.</p>

      <p>If you have any questions about this payment or your subscription, please don't hesitate to contact our support team.</p>

      <p>Thank you for using FaithYoke to manage your church!</p>

      <p style="margin-top: 30px;">
        Blessings,<br>
        <strong>The FaithYoke Team</strong>
      </p>
    </div>

    <div class="footer">
      <p style="margin: 0 0 10px 0;"><strong>FaithYoke</strong></p>
      <p style="margin: 0; font-size: 12px;">
        Church Management Platform<br>
        <a href="mailto:support@faithyoke.comm" style="color: #3b82f6; text-decoration: none;">support@faithyoke.comm</a> |
        <a href="https://faithyoke.comm" style="color: #3b82f6; text-decoration: none;">www.faithyoke.comm</a>
      </p>
    </div>
  </div>
</body>
</html>
    `.trim()
  }

  /**
   * Generate plain text email for payment confirmation
   */
  private static generatePaymentConfirmationText(params: {
    churchName: string
    adminName: string
    amount: number
    currency: string
    paymentMethod: string
    transactionReference: string
    paymentDate: string
    receiptNumber: string
  }): string {
    const formattedAmount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: params.currency
    }).format(params.amount)

    const formattedDate = new Date(params.paymentDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })

    return `
PAYMENT RECEIVED - ${params.receiptNumber}

Dear ${params.adminName},

We're pleased to confirm that we have received your payment for ${params.churchName}.

AMOUNT PAID: ${formattedAmount}

Payment Details:
-----------------
Receipt Number:  ${params.receiptNumber}
Payment Date:    ${formattedDate}
Amount Paid:     ${formattedAmount}
Payment Method:  ${params.paymentMethod.replace('_', ' ').toUpperCase()}
Reference:       ${params.transactionReference}

Your subscription has been activated and will remain active for one year from the payment date.

View your subscription details at:
https://admin.faithyoke.comm/settings?tab=subscription

If you have any questions about this payment or your subscription, please contact our support team at support@faithyoke.comm.

Thank you for using FaithYoke to manage your church!

Blessings,
The FaithYoke Team

---
FaithYoke - Church Management Platform
support@faithyoke.comm | www.faithyoke.comm
    `.trim()
  }

  /**
   * Send password reset email
   */
  static async sendPasswordResetEmail(params: {
    email: string
    adminName: string
    resetLink: string
  }): Promise<{ success: boolean; error?: string }> {
    const { email, adminName, resetLink } = params

    const htmlBody = this.generatePasswordResetHTML(params)
    const textBody = this.generatePasswordResetText(params)

    return await this.sendEmail({
      to: [{ email, name: adminName }],
      subject: '🔐 Reset Your FaithYoke Password',
      htmlBody,
      textBody
    })
  }

  /**
   * Generate HTML email for password reset
   */
  private static generatePasswordResetHTML(params: {
    adminName: string
    resetLink: string
  }): string {
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Reset Your Password</title>
  <style>
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 600px;
      margin: 0 auto;
      padding: 20px;
      background-color: #f4f4f4;
    }
    .container {
      background-color: #ffffff;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .header {
      background: linear-gradient(135deg, #0EA5E9 0%, #06B6D4 100%);
      color: white;
      padding: 30px;
      text-align: center;
    }
    .header h1 {
      margin: 0;
      font-size: 28px;
    }
    .content {
      padding: 30px;
    }
    .button {
      display: inline-block;
      padding: 14px 28px;
      background: linear-gradient(135deg, #0EA5E9 0%, #06B6D4 100%);
      color: white;
      text-decoration: none;
      border-radius: 8px;
      margin: 20px 0;
      font-weight: 600;
      font-size: 16px;
    }
    .warning-box {
      background-color: #FEF3C7;
      border-left: 4px solid #F59E0B;
      padding: 15px;
      margin: 20px 0;
      border-radius: 4px;
    }
    .security-note {
      background-color: #F0F9FF;
      border-left: 4px solid #0EA5E9;
      padding: 15px;
      margin: 20px 0;
      border-radius: 4px;
    }
    .footer {
      background-color: #f9fafb;
      padding: 20px;
      text-align: center;
      font-size: 14px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>🔐 Password Reset Request</h1>
      <p style="margin: 10px 0 0 0; font-size: 16px; opacity: 0.9;">FaithYoke Admin Portal</p>
    </div>

    <div class="content">
      <p>Dear ${params.adminName},</p>

      <p>We received a request to reset your password for your FaithYoke admin account.</p>

      <p>Click the button below to create a new password. This link will expire in <strong>1 hour</strong> for security reasons.</p>

      <p style="text-align: center;">
        <a href="${params.resetLink}" class="button">
          Reset My Password
        </a>
      </p>

      <div class="security-note">
        <p style="margin: 0; font-size: 14px;">
          <strong>🔒 Security Tips:</strong><br>
          • Create a strong password with uppercase, lowercase, numbers, and special characters<br>
          • Use a unique password you don't use elsewhere<br>
          • Never share your password with anyone
        </p>
      </div>

      <div class="warning-box">
        <p style="margin: 0; font-size: 14px;">
          <strong>⚠️ Didn't request this?</strong><br>
          If you didn't request a password reset, please ignore this email. Your password will remain unchanged. Someone may have entered your email address by mistake.
        </p>
      </div>

      <p style="font-size: 13px; color: #6b7280; margin-top: 30px;">
        If the button above doesn't work, copy and paste this link into your browser:<br>
        <a href="${params.resetLink}" style="color: #0EA5E9; word-break: break-all;">${params.resetLink}</a>
      </p>

      <p style="margin-top: 30px;">
        Blessings,<br>
        <strong>The FaithYoke Team</strong>
      </p>
    </div>

    <div class="footer">
      <p style="margin: 0 0 10px 0;"><strong>FaithYoke</strong></p>
      <p style="margin: 0; font-size: 12px;">
        Church Management Platform<br>
        <a href="mailto:support@faithyoke.com" style="color: #0EA5E9; text-decoration: none;">support@faithyoke.com</a> |
        <a href="https://faithyoke.com" style="color: #0EA5E9; text-decoration: none;">www.faithyoke.com</a>
      </p>
    </div>
  </div>
</body>
</html>
    `.trim()
  }

  /**
   * Generate plain text email for password reset
   */
  private static generatePasswordResetText(params: {
    adminName: string
    resetLink: string
  }): string {
    return `
PASSWORD RESET REQUEST - FaithYoke

Dear ${params.adminName},

We received a request to reset your password for your FaithYoke admin account.

Click the link below to create a new password. This link will expire in 1 hour for security reasons.

Reset Password Link:
${params.resetLink}

SECURITY TIPS:
• Create a strong password with uppercase, lowercase, numbers, and special characters
• Use a unique password you don't use elsewhere
• Never share your password with anyone

DIDN'T REQUEST THIS?
If you didn't request a password reset, please ignore this email. Your password will remain unchanged. Someone may have entered your email address by mistake.

If you have any questions, please contact our support team at support@faithyoke.com.

Blessings,
The FaithYoke Team

---
FaithYoke - Church Management Platform
support@faithyoke.com | www.faithyoke.com
    `.trim()
  }

  /**
   * Send subscription expiration warning
   */
  static async sendExpirationWarning(params: {
    churchName: string
    churchEmail: string
    adminName: string
    daysUntilExpiration: number
    expirationDate: string
  }): Promise<{ success: boolean; error?: string }> {
    const { churchName, churchEmail, adminName, daysUntilExpiration, expirationDate } = params

    const subject = `⚠️ Subscription Expiring in ${daysUntilExpiration} Days`
    const formattedDate = new Date(expirationDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })

    const htmlBody = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
        <h2 style="color: #f59e0b;">⚠️ Subscription Expiring Soon</h2>
        <p>Dear ${adminName},</p>
        <p>This is a friendly reminder that your FaithYoke subscription for <strong>${churchName}</strong> will expire in <strong>${daysUntilExpiration} days</strong> on <strong>${formattedDate}</strong>.</p>
        <p>To ensure uninterrupted service, please renew your subscription before the expiration date.</p>
        <p style="margin: 30px 0;">
          <a href="https://admin.faithyoke.comm/settings?tab=subscription" style="display: inline-block; padding: 12px 24px; background-color: #3b82f6; color: white; text-decoration: none; border-radius: 6px;">
            Renew Subscription
          </a>
        </p>
        <p>If you have any questions, please contact our support team.</p>
        <p>Blessings,<br>The FaithYoke Team</p>
      </div>
    `

    const textBody = `
Subscription Expiring Soon

Dear ${adminName},

This is a friendly reminder that your FaithYoke subscription for ${churchName} will expire in ${daysUntilExpiration} days on ${formattedDate}.

To ensure uninterrupted service, please renew your subscription before the expiration date.

Renew at: https://admin.faithyoke.comm/settings?tab=subscription

If you have any questions, please contact our support team.

Blessings,
The FaithYoke Team
    `.trim()

    return await this.sendEmail({
      to: [{ email: churchEmail, name: adminName }],
      subject,
      htmlBody,
      textBody
    })
  }
}

export default EmailService
